// src/modals/RescheduleModal.jsx (NEW FILE)

import React, { useState, useEffect } from "react";
import { toast } from "react-hot-toast";
import axiosInstance from "../context/axiosInstance";
import { motion } from "framer-motion";
import {
  X,
  Loader2,
  CalendarX,
  Clock,
  Send,
  Calendar as CalendarIcon,
} from "lucide-react";

const RescheduleModal = ({ oldBooking, onClose, onRescheduled }) => {
  const [date, setDate] = useState(
    oldBooking.date || new Date().toISOString().split("T")[0]
  );
  const [slots, setSlots] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [loading, setLoading] = useState(false);
  const [booking, setBooking] = useState(false);

  // Fetch available slots for the service/resource
  useEffect(() => {
    const fetchSlots = async () => {
      setLoading(true);
      setSelectedSlot(null);
      try {
        const endpoint =
          oldBooking.booking_type === "service"
            ? `/services/${oldBooking.entity_id}/slots/`
            : `/organisations/resources/${oldBooking.entity_id}/slots?date=${date}`;

        const payload =
          oldBooking.booking_type === "service"
            ? {
                owner_id: oldBooking.parent_id,
                service_id: oldBooking.entity_id,
                owner_type: "partner",
                date,
                service_duration: oldBooking.duration_minutes || 60,
                location_mode: oldBooking.location_mode || "office",
              }
            : null;

        const { data } = payload
          ? await axiosInstance.post(endpoint, payload)
          : await axiosInstance.get(endpoint);

        setSlots(data.slots || []);
      } catch (err) {
        toast.error("Failed to fetch available slots.");
        setSlots([]);
      } finally {
        setLoading(false);
      }
    };
    fetchSlots();
  }, [date, oldBooking]);

const handleReschedule = async () => {
  if (!selectedSlot) return toast.error("Please select a new time slot.");

  setBooking(true);
  const bookingToast = toast.loading("Rescheduling your booking...");

  try {
    const isService = oldBooking.booking_type === "service";

    // Step 1: Cancel the old booking
    const cancelEndpoint = isService
      ? `/services/bookings/${oldBooking.booking_id}/cancel/`
      : `/organisations/bookings/${oldBooking.booking_id}/cancel/`;
    await axiosInstance.post(cancelEndpoint);

    // Step 2: Book the new slot
    const confirmEndpoint = isService
      ? "/services/bookings/"
      : `/organisations/resources/${oldBooking.entity_id}/bookings/`;

    const confirmPayload = {
      date: date,
      // ✅ FIX: Use correct payload structure for each type
      ...(isService
        ? {
            start_time: selectedSlot.start_time,
            end_time: selectedSlot.end_time,
          }
        : { slots: [selectedSlot] }),
      user_id: oldBooking.user_id,
      userId: oldBooking.user_id, // For resource booking endpoint
      partner_id: oldBooking.parent_id, // For service booking
      service_id: isService ? oldBooking.entity_id : undefined,
    };

    await axiosInstance.post(confirmEndpoint, confirmPayload);

    toast.success("Booking rescheduled successfully!", { id: bookingToast });
    onRescheduled();
    onClose();
  } catch (err) {
    toast.error(
      err.response?.data?.error || "Rescheduling failed. Please try again.",
      { id: bookingToast }
    );
  } finally {
    setBooking(false);
  }
};
  const modalVariants = {
    hidden: { opacity: 0, scale: 0.95 },
    visible: { opacity: 1, scale: 1 },
    exit: { opacity: 0, scale: 0.95 },
  };
  // The JSX for this modal is almost identical to SlotBookingModal
  return (
    <div className="fixed inset-0 z-[110] flex items-center justify-center bg-background/80 backdrop-blur-md p-4 transition-all duration-300">
      <motion.div
        variants={modalVariants}
        initial="hidden"
        animate="visible"
        exit="exit"
        className="relative w-full max-w-lg bg-surface/90 backdrop-blur-xl border border-white/10 rounded-3xl shadow-2xl shadow-black/50 p-8"
      >
        {/* ✅ FIX: Added a close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-white transition-colors bg-white/5 hover:bg-white/10 rounded-full p-2"
        >
          <X size={20} />
        </button>

        <h2 className="text-2xl font-black tracking-tighter text-white mb-2">
          Reschedule Booking
        </h2>
        <p className="text-gray-400 mb-8">
          Select a new date and time for <span className="text-white font-medium">"{oldBooking.entity_name}"</span>.
        </p>

        {/* Date Picker */}
        <div className="mb-6">
          <label className="flex items-center gap-2 text-sm font-medium text-gray-400 mb-2 uppercase tracking-wider">
            <CalendarIcon size={16} /> Select New Date
          </label>
          <input
            type="date"
            value={date}
            min={new Date().toISOString().split("T")[0]}
            onChange={(e) => setDate(e.target.value)}
            className="w-full h-14 bg-white/5 border border-white/10 rounded-xl px-4 text-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500 focus:outline-none transition-all hover:bg-white/10"
          />
        </div>

        {/* Slots Grid */}
        <div className="min-h-[150px] pt-2 mb-6">
          {loading ? (
            <div className="flex flex-col items-center justify-center h-full text-gray-400 py-8">
              <Loader2 size={32} className="animate-spin mb-2 text-primary-500" />
              <p>Finding available slots...</p>
            </div>
          ) : slots.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-gray-400 text-center py-8 bg-white/5 rounded-2xl border border-white/5 border-dashed">
              <CalendarX size={40} className="mb-4 opacity-50" />
              <p className="font-semibold text-white text-lg">No Slots Available</p>
              <p className="text-sm">Please try a different date.</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {slots.map((slot, idx) => {
                const isSelected = selectedSlot === slot;
                // ✅ FIX: Check for both 'start' and 'start_time' to handle both resource and service slots
                const startTime = slot.start || slot.start_time;
                return (
                  <button
                    key={idx}
                    onClick={() => setSelectedSlot(slot)}
                    className={`p-3 rounded-xl text-center transition-all duration-300 border ${
                      isSelected
                        ? "bg-white text-black font-bold scale-[1.02] shadow-lg border-white"
                        : "bg-surface text-gray-300 border-white/10 hover:bg-white/5 hover:border-white/20"
                    }`}
                  >
                    <span className="flex items-center justify-center gap-2 text-sm">
                      <Clock size={14} className={isSelected ? "text-black" : "text-primary-400"} /> {startTime}
                    </span>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* ✅ FIX: Improved confirmation button */}
        <button
          onClick={handleReschedule}
          disabled={!selectedSlot || booking || loading}
          className="w-full bg-white text-black font-bold py-4 rounded-xl text-lg hover:bg-gray-100 transition-all flex items-center justify-center gap-2 disabled:opacity-50 hover:shadow-lg hover:scale-[1.02]"
        >
          {booking ? <Loader2 className="animate-spin" /> : <Send size={18} />}
          {booking ? "Rescheduling..." : "Confirm New Slot"}
        </button>
      </motion.div>
    </div>
  );
};

export default RescheduleModal;
